import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, session } from './api';
import type { AppModule, LoginResponse, ModuleAlias, User } from '../types';
import { disablePushSubscription } from './push';
import { normalizeModules } from './modules';

interface AuthValue {
  user: User | null;
  ready: boolean;
  modules: AppModule[];
  eId?: string;
  refresh(): Promise<void>;
  can(alias: ModuleAlias, permission?: 'read' | 'write' | 'delete'): boolean;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

function storedUser(): User | null {
  try { return JSON.parse(localStorage.getItem('user') || 'null') as User | null; } catch { return null; }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(storedUser);
  const [ready, setReady] = useState(false);
  const [modules, setModules] = useState<AppModule[]>([]);
  const [eId, setEId] = useState<string>();
  const refresh = useCallback(async () => {
    const current = await api.me();
    session.setUser(current.user);
    setUser(current.user);
    setModules(normalizeModules(current.modules));
    setEId(current.eId);
  }, []);
  useEffect(() => {
    if (!session.token()) { setReady(true); return; }
    refresh().catch(() => { session.clear(); setUser(null); setModules([]); }).finally(() => setReady(true));
  }, [refresh]);
  useEffect(() => {
    const update = () => void refresh().catch(() => undefined);
    window.addEventListener('nova:permissions-stale', update);
    return () => window.removeEventListener('nova:permissions-stale', update);
  }, [refresh]);
  const login = useCallback(async (username: string, password: string) => {
    const result: LoginResponse = await api.login(username, password);
    session.set(result); setUser(result.user); setModules(normalizeModules(result.modules)); setEId(result.eId);
  }, []);
  const logout = useCallback(async () => {
    try { await disablePushSubscription(); await api.logout(); } finally { session.clear(); setUser(null); setModules([]); setEId(undefined); }
  }, []);
  const can = useCallback((alias: ModuleAlias, permission: 'read' | 'write' | 'delete' = 'read') => Boolean(modules.find((item) => item.alias === alias)?.permissions[permission]), [modules]);
  const value = useMemo(() => ({ user, ready, modules, eId, refresh, can, login, logout }), [user, ready, modules, eId, refresh, can, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth trebuie folosit în AuthProvider');
  return value;
}
